import { useCallback, useState } from 'react'
import { Box, Checkbox, Paper, Stack, Typography } from '@mui/material'
import { MISC_PRIMARY } from '../../theme/onboardingTheme'

const CHECKLIST_ITEMS = {
  'licensee-details': 'Surplus Lines Licensee Details',
  'be-resident': 'Resident State and Surplus Lines License Number',
  'be-npn': 'National Producer Number (NPN)',
  'be-fein': 'FEIN',
  'ind-name': 'Full Name of Individual as it appears on their licenses',
  'ind-resident': 'Resident State and Surplus Lines License Number',
  'ind-npn': 'National Producer Number (NPN)',
  drlp: 'Designated Responsible Licensed Producer (DRLP) by entity, by state',
  logins: 'Surplus Lines Login Credentials (OPTins, State Portals, etc.)',
} as const

type ChecklistItemId = keyof typeof CHECKLIST_ITEMS

function emptyCheckedState(): Record<ChecklistItemId, boolean> {
  return Object.fromEntries(
    (Object.keys(CHECKLIST_ITEMS) as ChecklistItemId[]).map((id) => [id, false]),
  ) as Record<ChecklistItemId, boolean>
}

function ChecklistItem({
  id,
  label,
  checked,
  onToggle,
  boldLabel = false,
}: {
  id: ChecklistItemId
  label: string
  checked: boolean
  onToggle: (id: ChecklistItemId) => void
  boldLabel?: boolean
}) {
  return (
    <Box
      component="label"
      htmlFor={`prep-check-${id}`}
      sx={{
        display: 'flex',
        cursor: 'pointer',
        alignItems: 'flex-start',
        gap: 1.5,
        borderRadius: 1,
        py: 0.75,
        pr: 1,
        '&:hover': { bgcolor: '#f8fafc' },
      }}
    >
      <Checkbox
        id={`prep-check-${id}`}
        checked={checked}
        onChange={() => onToggle(id)}
        size="small"
        sx={{
          mt: 0.25,
          p: 0,
          color: '#cbd5e1',
          '&.Mui-checked': { color: MISC_PRIMARY },
        }}
      />
      <Typography
        component="span"
        sx={{
          fontSize: { xs: 14, sm: 16 },
          lineHeight: 1.625,
          fontWeight: boldLabel ? 700 : 400,
          color: checked ? '#64748b' : '#1e293b',
          textDecoration: checked ? 'line-through' : 'none',
          textDecorationColor: checked ? '#94a3b8' : undefined,
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

function ChecklistGroup({
  title,
  itemIds,
  checked,
  onToggle,
}: {
  title: string
  itemIds: ChecklistItemId[]
  checked: Record<ChecklistItemId, boolean>
  onToggle: (id: ChecklistItemId) => void
}) {
  return (
    <Box>
      <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 500, color: '#334155' }}>
        {title}
      </Typography>
      <Box component="ul" role="list" sx={{ mt: 0.5, pl: { xs: 2, sm: 2.5 }, m: 0, listStyle: 'none' }}>
        {itemIds.map((id) => (
          <Box component="li" key={id} role="listitem">
            <ChecklistItem
              id={id}
              label={CHECKLIST_ITEMS[id]}
              checked={checked[id]}
              onToggle={onToggle}
            />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export function StepChecklist() {
  const [checked, setChecked] = useState(() => emptyCheckedState())

  const toggle = useCallback((id: ChecklistItemId) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        bgcolor: '#ffffff',
        p: { xs: 3, sm: 4, lg: 5 },
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      }}
    >
      <Stack spacing={3} sx={{ maxWidth: 768 }}>
        <Box>
          <Typography
            component="h3"
            sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, fontWeight: 600, color: '#0f172a' }}
          >
            Onboarding Prep Checklist
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: { xs: 14, sm: 16 }, lineHeight: 1.625, color: '#1e293b' }}>
            Before you begin, please have the following information on hand.
          </Typography>
        </Box>

        <Box component="section">
          <ChecklistItem
            id="licensee-details"
            label={CHECKLIST_ITEMS['licensee-details']}
            checked={checked['licensee-details']}
            onToggle={toggle}
            boldLabel
          />
          <Stack
            spacing={2.5}
            sx={{
              mt: 0.5,
              ml: { xs: 3.5, sm: 4 },
              borderLeft: '2px solid #e2e8f0',
              pl: { xs: 2.5, sm: 3 },
            }}
          >
            <ChecklistGroup
              title="For Business Entities"
              itemIds={['be-resident', 'be-npn', 'be-fein']}
              checked={checked}
              onToggle={toggle}
            />
            <ChecklistGroup
              title="For Individuals"
              itemIds={['ind-name', 'ind-resident', 'ind-npn']}
              checked={checked}
              onToggle={toggle}
            />
          </Stack>
        </Box>

        <Box component="section" role="list" sx={{ '& > * + *': { mt: 0.5 } }}>
          <Box role="listitem">
            <ChecklistItem
              id="drlp"
              label={CHECKLIST_ITEMS.drlp}
              checked={checked.drlp}
              onToggle={toggle}
              boldLabel
            />
          </Box>
          <Box role="listitem">
            <ChecklistItem
              id="logins"
              label={CHECKLIST_ITEMS.logins}
              checked={checked.logins}
              onToggle={toggle}
              boldLabel
            />
          </Box>
        </Box>

        <Typography
          sx={{
            borderTop: '1px solid #e2e8f0',
            pt: 2.5,
            fontSize: { xs: 14, sm: 16 },
            lineHeight: 1.625,
            color: '#1e293b',
          }}
        >
          You can save your work as you go, and when you&apos;re finished, just select{' '}
          <Typography component="span" sx={{ fontWeight: 600, color: '#0f172a', fontSize: 'inherit' }}>
            &ldquo;Send to MISC&rdquo;
          </Typography>
        </Typography>
      </Stack>
    </Paper>
  )
}
